import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, AlertCircle, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { clearFunnelContext, trackEvent, trackPageView } from '@/lib/analytics';
import { clearPreviewMode } from '@/lib/preview-mode';
import { consumePendingTikTokPurchase, trackTikTokFunnelEvent } from '@/lib/tiktok';
import { useTranslation } from 'react-i18next';
import { clearCheckoutReturn, takeCheckoutReturn } from '@/lib/facelock';

export default function CheckoutSuccess() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const [credits, setCredits] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    // Read once on arrival: where the flat-fee avatar was bought from.
    const [backTo] = useState(() => takeCheckoutReturn());
    useEffect(() => { clearCheckoutReturn(); }, []);
    const [error, setError] = useState<string | null>(null);
    const hasTrackedPurchaseRef = useRef(false);

    const fetchCredits = async () => {
        try {
            const response = await api.getCredits();
            setCredits(response.data?.balance || 0);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch credits:', err);
            setError(t('checkout.loadError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Check if this is a fresh checkout or just visiting the page
        const sessionId = searchParams.get('session_id');
        const isFreshCheckout = sessionId !== null;

        trackPageView('checkout_success', { isFreshCheckout });
        
        fetchCredits();

        const trackPurchaseForFreshCheckout = async () => {
            if (!isFreshCheckout || hasTrackedPurchaseRef.current) return;

            const purchasesResult = await api.getPurchases();
            const purchaseFromServer = purchasesResult.success && purchasesResult.data?.purchases?.length
                ? [...purchasesResult.data.purchases]
                    .sort((left, right) => Date.parse(right.created_at) - Date.parse(left.created_at))[0]
                : null;
            const pendingPurchase = !purchaseFromServer ? consumePendingTikTokPurchase() : null;

            const didTrackFromServer = trackTikTokFunnelEvent('Purchase', {
                packType: purchaseFromServer?.pack_type || null,
                valueMxn: Number(purchaseFromServer?.amount_cents || 0) / 100,
            });

            if (!didTrackFromServer) {
                trackTikTokFunnelEvent('Purchase', {
                    packType: pendingPurchase?.contentId || null,
                    name: pendingPurchase?.name || null,
                    valueMxn: pendingPurchase?.valueMxn || null,
                });
            }

            trackEvent('purchase_completed', {
                source: purchaseFromServer?.pack_type ? 'purchase_history' : 'pending_checkout_context',
                packType: purchaseFromServer?.pack_type || pendingPurchase?.contentId || null,
                valueMxn: purchaseFromServer?.amount_cents
                    ? Number(purchaseFromServer.amount_cents) / 100
                    : pendingPurchase?.valueMxn || null,
                sessionId,
            });
            clearFunnelContext();
            clearPreviewMode();

            hasTrackedPurchaseRef.current = true;
        };

        trackPurchaseForFreshCheckout();

        // Auto-refresh credits after 3 seconds (give webhook time to process)
        if (isFreshCheckout) {
            const timer = setTimeout(() => {
                fetchCredits();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [searchParams]);

    const handleRefresh = async () => {
        setLoading(true);
        await fetchCredits();
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center p-4 bg-background">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md border border-border p-8 rounded-lg shadow-sm text-center bg-card text-card-foreground"
            >
                {/* Success Icon */}
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                    <Check className="w-8 h-8 text-green-600" />
                </motion.div>

                <h1 className="text-2xl font-bold mb-2 text-foreground">{t('checkout.paymentSuccessful')}</h1>
                
                <p className="text-muted-foreground mb-6">{t('checkout.thankYou')}</p>

                {/* Credits Display */}
                <div className="bg-muted rounded-lg p-4 mb-6">
                    <p className="text-sm text-muted-foreground mb-1">{t('checkout.currentBalance')}</p>
                    {loading ? (
                        <div className="flex items-center justify-center gap-2">
                            <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                            <span className="text-muted-foreground">{t('checkout.loading')}</span>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center gap-2 text-destructive">
                            <AlertCircle className="w-5 h-5" />
                            <span>{error}</span>
                            <button 
                                onClick={handleRefresh}
                                className="text-primary underline ml-2"
                            >
                                {t('checkout.retry')}
                            </button>
                        </div>
                    ) : (
                        <p className="text-3xl font-bold text-primary">
                            {t('checkout.creditsSuffix', { credits })}
                        </p>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                    {/* Paid from the avatar dialog (the flat-fee avatar): back to where it was bought. */}
                    {backTo && (
                        <Link
                            to={backTo.path}
                            data-testid="checkout-continue"
                            className="block w-full bg-primary text-primary-foreground py-3 rounded-md hover:opacity-90 transition-opacity font-medium"
                        >
                            {backTo.label === 'storybook' ? t('faceLock.pay.continueStorybook') : t('faceLock.pay.continueAvatar')}
                        </Link>
                    )}
                    <Link
                        to="/director"
                        className="block w-full bg-primary text-primary-foreground py-3 rounded-md hover:opacity-90 transition-opacity font-medium"
                    >
                        {t('checkout.startCreating')}
                    </Link>
                    
                    <div className="flex gap-3">
                        <Link
                            to="/pricing"
                            className="flex-1 border border-input py-2 rounded-md hover:bg-accent transition-colors text-foreground flex items-center justify-center"
                        >
                            {t('checkout.buyMore')}
                        </Link>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="flex-1 border border-input py-2 rounded-md hover:bg-accent transition-colors text-foreground flex items-center justify-center gap-2"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            {t('checkout.refresh')}
                        </button>
                    </div>
                </div>

                {/* Help Text */}
                <p className="text-sm text-muted-foreground mt-6">
                    {t('checkout.helpLead')} <Link to="/chopshop" className="text-primary underline">{t('checkout.chopShop')}</Link>.
                </p>
            </motion.div>
        </div>
    );
}
